import React from 'react';
import { Typography } from 'antd';
import styles from './Terms.module.scss';

const { Title } = Typography;

const Terms = () => {
  return (
    // prettier-ignore
    <div className={styles.termsOfConditionsContainer}>
      <Title level={3}>NEWTONX EXPERT TERMS AND CONDITIONS</Title>
      <span>Last updated October 2025</span>
      <br />
      <br />
      <Title level={3}>OVERVIEW AND ACTIVITIES</Title>
      <p>
        NewtonX, Inc. (“NewtonX”, “we” or “us”) is a primary market research firm that facilitates expertise sharing among
        professionals. The NewtonX experts are industry professionals (“Experts” or “you”) who educate and share
        insights with various organizations interested in their expertise (“Clients”) through the NewtonX platform.
        NewtonX provides opportunities for Experts to participate in expertise sharing (“Projects”), including, but not
        limited to consultations and online surveys. All Experts are subject to the following terms and conditions
        (“Terms”). These terms and conditions set out the legal and compliance obligations applicable to Experts. All
        Experts are governed by NewtonX’s Expert Terms and Conditions as well as all other documents referred to in
        them.
      </p>
      <p>Content refers to, among other things, educational information, consultations, interviews, surveys, presentations,
        recordings, videos, or any other content that is produced by NewtonX following your participation in a Project.</p>
      <br />
      <Title level={3}>NEWTONX CONDITIONS OF MEMBERSHIP AND PARTICIPATION IN PROJECTS</Title>
      <p>
        You are solely responsible for determining whether you are permitted to join NewtonX and to participate in
        Projects. Before applying to join NewtonX, you must review any and all legal agreements that could restrict your
        ability to participate, including, without limitation, employment agreements, consulting agreements,
        non-disclosure agreements, employee handbooks, codes of ethics and similar employment policies. If your
        employer or client agreement forbids you from participating in expert work such as the one offered by NewtonX,
        you also confirm that you have obtained consents or waivers from your clients, employers or any other third
        parties to ensure that you are permitted to act as an Expert.
      </p>
      <p>By agreeing to participate in any Project you confirm that you are knowledgeable about the proposed subject
        matter. As an Expert, you will be expected to provide information during any Project in good faith, to the best of
        your ability and at all times consistent with these Terms & Conditions.</p>
      <p>You agree that in participating in any Project, you will be acting in your personal capacity as an independent
        consultant, and not as an employee or representative of NewtonX or any other party. You agree that your
        participation in any Project is at your sole discretion. You further agree that you have no authority to act on
        behalf of NewtonX and are not eligible for any NewtonX or Client employment benefits based on your
        participation in the NewtonX Projects. You shall not identify any Client or NewtonX as your employer.
        Additionally, Experts are never obligated to accept or continue any Project and may decline to participate or
        continue to participate in any Project at any time.</p>
      <p>At any time prior to, during, or after a Project you become aware of any conflict of interest or perceived conflict
        of interest, or any other professional, ethical, or regulatory conflict that would preclude you from participating,
        you should terminate the Project and notify NewtonX immediately via email at <a href="mailto:compliance@newtonx.com">compliance@newtonx.com</a>.
        You understand that NewtonX is not obligated to engage you to participate in Projects and can determine, at
        any time, in its sole discretion, that you have breached these Terms and Conditions, resulting in the suspension
        or termination of your relationship with NewtonX.</p>
      <br />
      <Title level={3}>EXPERT OBLIGATIONS</Title>
      <p>You represent, confirm, and warrant that:</p>
      <ol type="a">
        <li>Accurate Representation
          <ol type="i">
            <li>You have provided NewtonX with accurate and updated biographical information and
              employment history and in connection with any further queries, including, without limitation,
              your current and former employment, and any conflicts or restrictions on your ability to
              consult. Furthermore, you understand that NewtonX, Clients and third parties may and will rely
              on your Expert Information and agree never to disseminate false or misleading information or
              inaccurately represent your experience, education, employment, or knowledge to us or any
              Clients. Based on further information from you or other sources, we may amend your Expert
              Information and you acknowledge that you are under a continuing obligation to monitor the
              accuracy of your Expert Information and to update changes promptly.</li>
          </ol>
        </li>
        <li>Confidentiality
          <ol type="i">
            <li>You will not participate in any Project that causes you to breach any agreement with or other
              legal obligation to any person or entity (including, but not limited to, your employer, your
              previous employers or any person or entity to which, or through which, you provide or have
              provided consulting services).</li>
            <li>You will not participate in any Project that would result in the disclosure of any material
              nonpublic information, confidential or proprietary information (including, but not limited to;
              trade secrets), or any such information. Additionally, you will not disclose any information that
              you are otherwise prohibited to discuss under applicable law or which could result in
              breaching any regulatory, legal, ethical, or professional standard or rule.</li>
          </ol>
        </li>
        <li>Employment
          <ol type="i">
            <li>You agree not to participate in consultations about a company for which you are a current
              employee, director, trustee, officer, contractor, agent, legal representative, partner, joint
              venturer, or board member (or for which you hold a similar position). You further agree that you
              will not participate in call consultations about that company until six months after termination
              of your employment.
            </li>
            <li>You agree not to participate in consultations on a Project for a Client that is a direct
              competitor of a company for which you are a current director, trustee, officer, board member
              or employee (or for which you hold a similar position). You will not knowingly participate in any
              consultation for a Client that is (or is acting on behalf of) a competitor of your current
              employer or a company for which you serve as an officer or director.
            </li>
            <li>If a public company of which you are a current director, trustee, officer, board member or
              employee (or for which you hold a similar position) is the target or bidder in a tender offer, or
              has filed to issue an IPO, you must decline all consultations until the tender offer is resolved or
              the IPO is issued.
            </li>
            <li>Certain market research Projects conducted through NewtonX (Surveys, AI-Moderated
              Qualitative interviews), will be designed to be conducted on a blinded basis such that your
              identity and the identity of your current employer (if any) will not be shared with the client.
              <ol type="1">
                <li>Despite NewtonX’s limitations on consulting about your employer, for these blinded
                  Projects, you may be asked to discuss non-confidential insights related to your
                  employer.
                </li>
                <li>As with all NewtonX Projects, you must not share confidential information and must
                  honor your obligations to your current and former employers (if any) and any other
                  entities to which you have such obligations.
                </li>
              </ol>
            </li>
            <li>NewtonX will, from time to time, inform you of additional rules that you are required to observe
              and specific topics that you are not permitted to discuss. You agree to observe these rules
              and any limitations we place on what you can discuss.
            </li>
          </ol>
        </li>
        <li>Misconduct and Sanctions
          <ol type="i">
            <li>You represent that you have not been convicted of or charged with any financial crimes (
              including but not limited to insider trading, market abuse, money laundering, fraud) or any
              felony.
            </li>
            <li>You represent that you are not, and have not been, the target or subject of any regulatory or
              enforcement proceeding brought by any securities, commodities or other financial regulatory
              authority, self-regulatory organization, or other law enforcement body.
            </li>
            <li>You represent that you have not been debarred, excluded, suspended or otherwise not
              permitted to participate in any government or state programs.</li>
            <li>You represent that you are not, and have not been, the subject of any economic sanctions,
              embargoes, or debarment maintained by the United States, the United Kingdom, the European
              Union, or the United Nations
            </li>
            <li>You represent that you will not cause NewtonX to be in violation of any such sanctions. </li>
            <li>You agree that you will not act for, or provide or confer a benefit directly or indirectly to, or
              facilitate or support a transaction by
              <ol type="1">
                <li>any government, individual, entity or vessel on applicable sanctions lists or
                </li>
                <li>any country or region subject to comprehensive sanctions in violation of U.S., UK, or EU
                  law.
                </li>
              </ol>
            </li>
          </ol>
        </li>
        <li>Code of Conduct
          <ol type="i">
            <li>You agree to at all times comply and adhere to the NewtonX Expert Code of Conduct (as may
              be updated from time to time).
            </li>
          </ol>
        </li>
        <li>Clinical Trials
          <ol type="i">
            <li>You agree to inform NewtonX if you are currently involved in or have access to unpublished
              clinical trial results, patient information, or any other information that has not yet been made
              public.
            </li>
            <li>You agree not to discuss unpublished clinical trial results, patient information, or any other
              information that has not yet been made public.
            </li>
          </ol>
        </li>
        <li>Advice
          <ol type="i">
            <li>You will not provide any investment advice (including, without limitation, advice concerning the
              value of any security or the advisability of buying, selling, or otherwise investing in any
              security), medical advice, legal advice, or any other type of advice to any Client.
            </li>
          </ol>
        </li>
        <li>Healthcare Professionals
          <ol type="i">
            <li>You agree not to disclose any patient or other information prohibited to be disclosed under
              The Health Insurance Portability and Accountability Act of 1996 (HIPAA) Privacy and Security
              Rules or other similar laws, rules or regulations.
            </li>
            <li>You agree not to dispense any medical advice at any time.
            </li>
            <li>You certify to NewtonX that you are duly licensed in any jurisdictions in which you are currently
              practicing, and that you are not listed on the List of Excluded Individuals/Entities issued by the
              Office of the Inspector General of the U.S. Department of Health and Human Services and/or
              the Debarment List of the U.S. Food and Drug Administration. You agree that you will promptly
              notify NewtonX in the event that any of the representations in this paragraph cease to be true.
            </li>
          </ol>
        </li>
        <li>Attorneys and Auditors
          <ol type="i">
            <li>If you are an attorney, you agree not to participate in consultations about any Clients you
              currently represent or to disclose any information or opinions that would breach any legal or
              ethical duty you or your firm owes to past or present Clients.
            </li>
            <li>If you are currently an auditor or formerly an auditor, you agree not to participate in
              consultations about companies or other organizations that you currently audit or have audited
              within the last three years.
            </li>
            <li>You represent that you are duly licensed to practice in the jurisdictions in which you currently
              practice, and that you are in good standing as a member of the Bar or other similar
              professional boards to which you are subject.
            </li>
            <li>You agree not to provide any legal or professional advice or services to a Client during a
              Project for that Client.
            </li>
          </ol>
        </li>
        <li>Government Employees
          <ol type="i">
            <li>If you are a government employee in a government or jurisdiction that forbids government
              employees from participating in outside consulting projects, you may not be an Expert of
              NewtonX or participate in any Project.
            </li>
            <li>Otherwise, if you are a government employee, you agree not to consult about matters
              presently regulated by the agency or department in which you are employed.
            </li>
            <li>You agree not to consult about any legislation, regulation, or policy that you are in a position to
              vote upon or otherwise influence, and you agree not to give or receive any item of value from
              or to another person or entity for the purpose of seeking influence or for any other improper
              purpose.
            </li>
          </ol>
        </li>
        <li>Citizens or Residents of the People’s Republic of China
          <ol type="i">
            <li>You represent that you are not employed by the Chinese government or a Chinese government
              agency.
            </li>
            <li>You must comply with your duties and obligations under relevant laws and regulations
              (including but not limited to the Guarding State Secrets Law of the People’s Republic of China,
              the National Security Law of the People’s Republic of China and Anti-Espionage Law of the
              People’s Republic of China), and you must not discuss or disclose such information if you have
              or had access to:
              <ol type="1">
                <li>information relating to China’s national security, interests and/or national core data,
                </li>
                <li>non-public statistical data related to the Chinese economy or market
                </li>
                <li>other important data related to the Chinese economy or market
                </li>
                <li>other important data defined by applicable laws and/or regulations, or
                </li>
                <li>other information which may be non-compliant with applicable laws and/or regulations,
                </li>
              </ol>
            </li>
          </ol>
        </li>
      </ol>
      <p>YOU MUST DECLINE OR DISCONTINUE PARTICIPATION IN ANY PROJECT THAT WOULD RESULT IN A VIOLATION OF
        ANY OF THE CONDITIONS ABOVE.</p>
      <br />
      <Title level={3}>LEGAL PRIVILEGE</Title>
      <p>
        In the event that a Client initiates a Project involving its legal counsel, it is possible that the Client may wish to
        assert a claim of legal privilege concerning the Project. Under these circumstances, Client’s legal counsel may
        ask you to enter into a separate written engagement agreement with them for your services (an “Engagement
        Agreement”). Any such request may only come through NewtonX, and you may only choose to accept following
        NewtonX’s prior written approval in its sole discretion. Once approved, an Engagement Agreement shall control
        any inconsistent terms in these Terms & Conditions with respect to your relationship and obligations to such
        Client with respect to the Project. Such Engagement Agreement shall not modify your obligations to NewtonX or
        third parties, under these Terms & Conditions or otherwise.
      </p>
      <br />
      <Title level={3}>PRIVACY</Title>
      <p>You have read and agree to NewtonX’s Privacy Policy (as updated from time to time).</p>
      <p>
        You agree that NewtonX may collect, synthesize and retain information about you, from sources including,
        without limitation, yourself, your activities through NewtonX, and third-party sources (hereafter, “Personal
        Information”). You furthermore agree that NewtonX may share results of consultations and surveys with its
        clients. NewtonX will protect Personal Information in accordance with NewtonX’s Privacy Policy, which is
        incorporated by reference. NewtonX will not sell Personal Information to third-party marketers or similar
        organizations, or disclose the Personal Information publicly, unless you provide consent or we determine that it
        is appropriate in response to a law, regulation, rule, government request or legal process. If your membership in
        NewtonX ends, you agree that NewtonX may preserve Personal Information, provided that NewtonX accords
        such Personal Information the protections set forth in its then-current Terms & Conditions privacy policies.
        NewtonX also commits to let you ask for deletion of your personal information. Notwithstanding the latter, you
        agree that NewtonX may use and share Personal Information in order to, without limitation, contact you
        concerning your participation in NewtonX, manage your membership in the NewtonX community, administer
        compliance policies and procedures, including obtaining required approvals and consents for Project
        participation, market NewtonX ’s services, and comply with applicable laws and requests from government
        authorities. As part of its compliance screening, NewtonX reserves the right to conduct background checks on
        you, itself or utilizing third-party services, and to confirm your professional and educational history. You agree
        that we may disclose Personal Information as required by law, to protect NewtonX’s rights, or for the prevention,
        detection or disclosure of a crime. You also recognize that Clients may be required by law or their compliance
        policies to disclose Personal Information about their Projects with you and you authorize NewtonX and its
        Clients to make such disclosures.
      </p>
      <br />
      <Title level={3}>APPLICATION OF THE TERMS & CONDITIONS</Title>
      <p>
        By becoming an Expert, you represent and warrant to us that you are in compliance with each of the conditions,
        agreements, acknowledgments, representations, covenants, warranties and other obligations included in these
        Terms & Conditions. You understand and acknowledge that each NewtonX employee, affiliate and Client is an
        intended third-party beneficiary of these Terms & Conditions and shall therefore have the right to enforce your
        compliance with them. You must decline to participate in, or to continue your participation in, any Project or
        other activity that would cause you to violate these Terms & Conditions and immediately inform us of the
        reason. We typically do not participate in your interactions with Clients and shall not be liable or responsible to
        you in any manner whatsoever for Projects arranged by us. You are solely responsible for ensuring your
        continued compliance with the Terms & Conditions. You agree to cooperate fully in any inquiry concerning
        actual, alleged, or potential violations of these Terms & Conditions. To encourage your vigilance and care in
        complying with these Terms & Conditions, you may submit a payment request for a full Project that you choose
        to terminate early for the purpose of complying with them.
      </p>
      <br />
      <Title level={3}>CONFIDENTIALITY</Title>
      <p>
        You shall keep and maintain as strictly confidential, and agree not to communicate, reveal or disclose, all
        “Confidential Information” of NewtonX or its Clients, without NewtonX’s expressly written permission (which it
        may withhold in its sole discretion). “Confidential Information” includes any and all nonpublic information, in any
        form or medium, written, oral or otherwise, concerning or relating to NewtonX, any of its affiliates or any Client,
        including, but not limited to, the names of any Clients and Clients’ employees, questions posed by any Client and the topics discussed, information or materials shared, opinions expressed or work commissioned by
        NewtonX or any Client. In the event that you are required to disclose Confidential Information under applicable
        law, you agree to promptly notify NewtonX and to fully cooperate with NewtonX in exercising any right or
        exemption available to prevent the disclosure of Confidential Information to the extent permitted under
        applicable law. You shall not use Confidential Information for any purpose other than those specifically
        permitted by these Terms & Conditions. Without limiting the foregoing, you shall not use Confidential
        Information for personal benefit (including for the purpose of making investment or trading decisions). The duty
        to maintain confidentiality hereunder shall survive until the Confidential Information becomes generally known
        to and available for use by the public other than as a result of a disclosure by you or any other person under
        legal obligation not to disclose the information. NewtonX does not guarantee that you will not be exposed to any
        material nonpublic information and you remain solely responsible for not violating any applicable laws when
        using any information in connection with work commissioned by NewtonX or any Client.
      </p>
      <br />
      <Title level={3}>PROJECTS</Title>
      <p>
        You shall only accept Projects with NewtonX Clients individually arranged or explicitly approved by NewtonX. If a
        Client requests additional work or Projects, you must receive explicit approval from us before commencing work.
        You will not be paid for any Project with a NewtonX Client that was not individually arranged or explicitly
        approved by NewtonX in advance, even if the Project was a follow-up to a Project that was arranged by
        NewtonX. If you are invited by NewtonX to perform a Project for a Client, you should either accept or decline the
        invitation as soon as reasonably possible. We make no representation regarding the frequency or quantity of
        Project requests that you will receive. We expect you to perform all Projects in a timely, diligent and professional
        manner consistent with the highest industry practice.
      </p>
      <br />
      <Title level={3}>PAYMENT</Title>
      <p>
        You will be paid based on your customary consulting fees, as agreed by you and us. You may only change your
        consulting fee rates upon written notice to us and the new fee rate is only applicable to Projects that occur
        after our receipt and approval of written notice. You may not reveal your billing rates and financial arrangements
        to Clients without NewtonX’s prior written consent. Our payment system may accommodate consulting
        guidelines set forth by an Expert’s employer, including joint payment to the Expert and his or her employer,
        direct payment to the Expert’s institution or payment to a charity of the Expert’s choice. Experts will receive a
        U.S. I.R.S. Form 1099, as required. Payments will be made following the completion of an Expert’s consulting
        engagement, within 30 days.
      </p>
      <br />
      <Title level={3}>INDEPENDENT CONTRACTOR STATUS</Title>
      <p>
        It is understood and agreed that any services performed by you under these Terms & Conditions shall be
        performed as an independent contractor and consultant, and you further understand and acknowledge that you
        have no authority to act or speak on NewtonX’s behalf or to represent that you have any such authority. You
        shall not be deemed to be an employee of NewtonX or any of its Clients and you shall not be entitled to any
        benefits provided by either NewtonX or any of its Clients. You agree that you shall be personally responsible and
        liable for any and all taxes and other payments due on payments received by you for services provided under
        these Terms & Conditions. Nothing in these Terms & Conditions shall create a partnership or joint venture or
        establish the relationship of principal and agent or any other relationship of a similar nature between you,
        NewtonX or its Clients.
      </p>
      <br />
      <Title level={3}>TERMINATION</Title>
      <p>
        You may terminate your membership in NewtonX at any time by providing us with written notice. Likewise,
        NewtonX may terminate your membership in NewtonX at any time and for any reason without providing notice.
        Termination shall not relieve either party of its obligations under these Terms & Conditions. The Terms &
        Conditions are intended to survive termination, which include, but are not limited to: Privacy, Application of the
        Terms and Conditions, Confidentiality, Non-Solicitation, Intellectual Property, Indemnification, Limitation of
        Liability, Arbitration, Governing Law and Venue and Miscellaneous.
      </p>
      <br />
      <Title level={3}>NON-SOLICITATION</Title>
      <p>
        You agree that for a period of one year from the completion of any Project with a Client to whom you have been
        first introduced by us, you will not circumvent NewtonX by knowingly soliciting, negotiating with, or entering into
        any agreement or other arrangement (whether written or oral) with a Client first introduced to you by us
        pursuant to which you would be engaged by such Client to provide services of a similar nature to the services,
        Experts provide to NewtonX’s Clients (i.e., short term consulting services) without our prior written consent. You
        also agree that, for a period of one year after the termination of your relationship with us for any reason
        whatsoever, you will not, directly or indirectly, solicit for employment, employ or hire any employee of NewtonX
        or of any Clients to whom you have been first introduced by us. It does not apply to the extent that you have a
        proven pre-existing relationship or association, based on past work performed. If you notify NewtonX of such a
        pre-existing relationship when a Client is proposed to you, and you do not participate in a Consultation with the
        Client, NewtonX imposes no restrictions with regard to this Client. NewtonX will not restrict you from accepting
        an offer of employment from one of its Clients.
      </p>
      <br />
      <Title level={3}>INTELLECTUAL PROPERTY</Title>
      <p>
        You are solely responsible for the content that you share or create in connection with your performance of
        services hereunder. You represent that you own or have obtained any necessary licenses or permissions for any
        materials, regardless of form or format (hereafter, “IP”), that you provide to NewtonX, Clients or third parties in
        connection with your performance of such services.
      </p>
      <p>
        All Intellectual Property rights in any material provided to you by NewtonX or contained on the NewtonX website
        belong to NewtonX and may not be reproduced or redistributed.
      </p>
      <p>
        IP that you create in connection with any Client Project shall be solely owned by the Client who requested the
        Project and that Client may use such IP for any purpose permitted under applicable law and that Client’s
        agreement with NewtonX. Any inventions, discoveries or improvements that are based in any way on any IP you
        create for a Client in the course of a Project, and all intellectual property rights in such inventions, discoveries or
        improvements, shall likewise be owned solely and entirely by the Client. Any IP that you created prior to, or
        independently of, any Project (hereafter, “Expert IP”) remains your property. However, with respect to any
        Expert IP that you share or otherwise provide to a Client in connection with a Project, you shall automatically
        grant that Client a perpetual, worldwide, royalty-free, and transferable license to use such Expert IP.
      </p>
      <p>
        Furthermore, and without limiting the foregoing, you agree that each Client is free to use any ideas, concepts,
        know-how, or techniques contained in any IP you share with or otherwise provide to that Client for any purpose
        whatsoever, including, but not limited to, devising, manufacturing, promoting and selling products and services
        using such information.
      </p>
      <p>
        Should you participate in any consultation between you and a NewtonX Client, whereby the Client agrees the
        consultation can be recorded, transcribed, and/or distributed by NewtonX to third parties, you acknowledge
        that your Consultations with the Client may be recorded by or on behalf of NewtonX (‘Recordings”), and by
        participating in said Consultation you consent to such Recordings and the production of Transcripts by
        NewtonX (“Transcripts”). In doing so:
      </p>
      <ol type="a">
        <li>You agree that NewtonX will be the sole owner of all Intellectual Property rights (including without
          limitation copyright) in Recordings and Transcripts;
        </li>
        <li>You assign to NewtonX all Intellectual Property rights (including without limitation copyright) in such
          Recordings and Transcripts on an exclusive basis, without any territorial limit or royalty, and agree that
          NewtonX will have the right to use, distribute, reproduce, publish, modify, edit, summarise, adapt,
          sublicense, in whole or in part, in any language or format and in all media such Recordings and
          Transcripts (and the contents thereof) for any commercial or non-commercial purpose. Such use may
          include using the Transcript to produce its own content based (in whole or in part) on such content
          and that the assignment will be effective immediately when the Recording is made and you agree to
          execute such documents or take such other steps as may be necessary to give full effect to such
          assignment;
        </li>
        <li>You irrevocably waive any and all moral rights in any Recordings and Transcripts (and the contents
          thereof) in favour of NewtonX; and
        </li>
        <li>Your name will not be included in such Transcripts which are distributed to third parties.
        </li>
        <li>Unless otherwise agreed with a Client in writing, you warrant that any documentation or any other
          material that you create independently of or prior to a Consultation (including without limitation
          reports, studies, data, diagrams, charts, specifications, and programs) (“Work Product”) provided by you
          to a Client are original works and do not infringe any third party’s Intellectual Property rights and you
          hereby agree to grant to the Client (and/or Client’s client) to which you supply such Work Products a
          non-exclusive, royalty-free, perpetual licence to use any and all Intellectual Property rights subsisting in
          such Work Products in the course of its business.
        </li>
        <li>You will indemnify NewtonX against all liabilities it may incur in connection with any claims or
          proceedings brought against it based on a claim that the Work Product provided to a Client infringes
          any Intellectual Property rights or other proprietary rights of any third party.
        </li>
        <li>You must not use the name “NewtonX” or any other trademark, symbol or logo of NewtonX without
          NewtonX’s prior written consent.
        </li>
      </ol>
      <br />
      <Title level={3}>INDEMNIFICATION</Title>
      <p>
        You agree to indemnify and hold harmless NewtonX and its affiliates, and their respective officers, directors,
        employees, members, managing members, managers, agents, representatives, successors and assigns
        (collectively, the “NewtonX Parties”) from any and all actions, causes of action, claims, charges, demands, cost,
        expenses and damages (including attorneys’ fees and expenses) and liabilities of any kind whatsoever directly
        or indirectly resulting from, arising out of or in connection with your breach or violation of any and all provisions
        of these Terms and Conditions.
      </p>
      <br />
      <Title level={3}>LIMITATION OF LIABILITY</Title>
      <p>
        NewtonX’s total liability in contract, tort, misrepresentation or otherwise arising in connection with this
        Agreement shall be limited to an amount equal to the fees paid to you.
      </p>
      <p>
        NewtonX shall not be liable for any loss or damage, or any costs, expenses or other claims including without
        limitation:(i) loss of profit: (ii) loss of business; (iii) loss of revenue; (iv) loss of goodwill; (v) loss of anticipated
        savings; (vi) loss of any data or information and/or (vii) special or indirect loss or consequential loss or
        otherwise which arise out of any Consultation or in connection with this Agreement.
      </p>
      <p>
        Nothing in the Agreement excludes or limits NewtonX’s liability for death or personal injury caused by its
        negligence or for fraudulent misrepresentation.
      </p>
      <br />
      <Title level={3}>ARBITRATION, GOVERNING LAW AND VENUE</Title>
      <p>
        The validity, interpretation, performance and enforcement of these Terms & Conditions shall be governed by the
        laws of the State of New York without regard to the conflicts of laws principles thereof. ANY DISPUTE,
        CONTROVERSY OR CLAIM ARISING FROM OR RELATING TO THESE TERMS AND CONDITIONS SHALL BE FULLY
        AND EXCLUSIVELY FINALLY SETTLED BY AN ARBITRATION HELD IN NEW YORK CITY UNDER THE RULES OF THE
        AMERICAN ARBITRATION ASSOCIATION IN EFFECT FROM TIME TO TIME. YOU HEREBY IRREVOCABLY AND
        UNCONDITIONALLY WAIVE ANY RIGHTS YOU MAY HAVE TO A TRIAL BY JURY WITH RESPECT TO ANY SUCH
        DISPUTE, CONTROVERSY OR CLAIM RELATING TO THIS AGREEMENT. The arbitrator may grant any remedy that he
        or she deems just and equitable within the scope of this arbitration agreement, except that he or she may not,
        under any circumstance, grant a remedy inconsistent with or in violation of the Limitation of Liability. The award
        of the arbitrator shall be final and binding and judgment thereon may be entered in any court having jurisdiction.
        You hereby unconditionally and irrevocably submit to the jurisdiction of the federal and state courts of New
        York.
      </p>
      <br />
      <Title level={3}>MISCELLANEOUS</Title>
      <p>
        We may modify these terms and conditions from time to time by posting the modification(s) on our website,
        www.NewtonX.com. Unless otherwise specified by us when posted, all modifications will be effective upon
        posting. If you do not agree to any modification(s), your recourse will be to terminate your participation as an
        Expert in NewtonX. If you continue to participate as an Expert after any modification becomes effective, then
        your participation will constitute acceptance of such modification. No failure or delay by either party in
        exercising any right, power or privilege hereunder shall operate as a waiver thereof, nor shall any single or partial
        exercise thereof preclude any other or further exercise thereof or the exercise of any right, power or privilege
        hereunder. Neither these Terms & Conditions nor any of the rights, interests or obligations hereunder may be
        assigned, delegated or otherwise transferred, in whole or in part, by operation of law or otherwise, by you
        without our prior written consent (which consent we may withhold in our sole discretion), and any such
        assignment, delegation or transfer by you without such prior written consent shall be null and void and of no
        force or effect whatsoever. These Terms & Conditions shall be binding upon and inure solely to the benefit of
        you and NewtonX and the successors, assigns and Clients of NewtonX. As used in these Terms & Conditions, the
        word “including” shall mean “including, without limitation,” in all cases. Whenever possible, each provision of
        these Terms and Conditions shall be interpreted in such manner as to be effective and valid under applicable
        law, but if any provision of these Terms & Conditions is held to be invalid, illegal or unenforceable in any respect,
        such invalidity, illegality or unenforceability shall not affect any other provision of these Terms & Conditions, and
        these Terms & Conditions shall be reformed, construed and enforced as if such invalid, illegal or unenforceable
        provision had never been contained herein.
      </p>
      <p>By signing these Terms & Conditions, you acknowledge and agree that you are making express representations
        to NewtonX and Clients that you will abide by all of your obligations and responsibilities as set forth in these
        Terms & Conditions.</p>
    </div>
  );
};

export default Terms;
